import {
  brlInputToCents,
  centsToBRL,
} from '@/features/quotes/utils/rd-units.util';
import type {
  RdCoverageDef,
  RdCoverageSelectionItem,
  RdCoverageSelectionPayload,
  RdModalityCoverages,
  RdModalitySlug,
  RdProposalEquipment,
} from '@/services/quotes/rd-equipamentos/types';
import {
  deriveBasicaCents,
  describeBasicaRule,
  resolveLimit,
} from '@/features/quotes/utils/rd-equipamentos/rd-limit-rule.util';
import { formatCurrencyInputValue } from '@/shared/utils/currencyFormatter';

/**
 * `despesa_salvamento`: linha DERIVADA e não editável — vale 10% da Básica e
 * pronto (decisão do PO, 2026-09-03). O motor sobrescreve o que for enviado
 * (`Proposal._final_rows`), então deixá-la editável aqui só produziria um
 * campo que aceita digitação e depois muda de valor sozinho.
 *
 * A porcentagem NÃO está codificada aqui: sai do `limitRule` do cadastro
 * (`<=0.1*basica`), a mesma fonte que o motor usa.
 */
export const DESPESA_SALVAMENTO_SLUG = 'despesa_salvamento';

/** Mensagem por linha quando o LMI passa do teto da cobertura. */
export const OVER_LIMIT_MESSAGE = 'I.S. acima do limite para esta cobertura.';

/**
 * C4: a cobertura Básica (`coverageDef.mandatory`) é obrigatória e precisa
 * de LMI > 0 — hoje o motor aceita R$0,00 e floora em R$250 (item R$0), o
 * que não deveria ser possível de emitir. Mensagem alinhada ao vocabulário
 * de erro já usado pelo backend para esta matriz (`"I.S. acima do limite
 * para esta cobertura."`, ver `resolveCoverageFieldRowIndex`).
 */
export const BASICA_REQUIRED_MESSAGE = 'Informe a I.S. da cobertura Básica.';

export type CoverageRow = {
  coverageSlug: string;
  name: string;
  selected: boolean;
  /** Valor mascarado (BRL) exibido no input — mesma convenção de `fleetCoverages` em `step-frota.component.tsx`. */
  lmiDisplay: string;
  /** `coverageDef.mandatory` — exibe o pill "Básica" (mesmo padrão visual da cobertura obrigatória nos outros produtos). */
  mandatory: boolean;
  /** Regra de limite do cadastro (`<=basica&max:150000000`), como veio da API. */
  limitRule: string | null;
  /** Teto em centavos que a regra impõe neste contexto, ou `null` se não houver. */
  maxCents: number | null;
  /** Linha calculada pelo motor (cobertura automática) — exibida readonly. */
  derived: boolean;
  /**
   * Legenda da Básica ("110% do valor do equipamento"), lida da regra do
   * cadastro — ver `describeBasicaRule`. Ausente nas demais linhas.
   */
  basicaHint?: string;
  /** Se a cobertura pede período indenitário — decisão do CADASTRO. */
  usesIndemnityPeriod: boolean;
  /**
   * Período escolhido, em meses. `undefined` até o corretor escolher — e o
   * motor barra a cotação nesse estado (a gravação, não).
   */
  indemnityMonths?: number;
};

/**
 * Linhas de uma matriz de coberturas a partir da MODALIDADE, sem depender de
 * um item já criado no backend: uma linha por cobertura disponível em
 * `rateGrid[modalitySlug]` (ausência = indisponível para a modalidade — é
 * isso que faz as linhas diferirem entre móvel/estacionário/portátil).
 *
 * A cobertura `mandatory` (Básica) é sempre selecionada e o LMI dela é uma
 * projeção do valor do equipamento pela regra dela (`basicaContext`: 110% do
 * valor com `<=1.1*value`) — o motor deriva a mesma conta em
 * `Proposal.set_coverages` e descarta o que for enviado.
 *
 * `existing` hidrata as demais linhas quando o item já tem coberturas
 * salvas; omiti-lo produz a matriz "em branco" usada pelo formulário de
 * adicionar equipamento, onde nenhum item existe ainda.
 *
 * O teto de cada linha (`maxCents`) sai do `limitRule` do cadastro, resolvido
 * contra a Básica derivada (`basicaContext`). Antes o `limitRule`
 * chegava da API e era ignorado: o corretor só descobria o limite pelo 400 do
 * motor, depois de preencher.
 */
export function buildRowsForModality(
  modalitySlug: RdModalitySlug,
  valueCents: number,
  rateGrid: RdModalityCoverages,
  coverages: RdCoverageDef[],
  existing: {
    coverageSlug: string;
    selected: boolean;
    lmi: number;
    indemnityMonths?: number;
  }[] = []
): CoverageRow[] {
  const availableSlugs = rateGrid[modalitySlug] ?? [];
  const coverageDefBySlug = new Map(coverages.map(c => [c.slug, c]));
  const existingBySlug = new Map(existing.map(c => [c.coverageSlug, c]));
  const ctx = basicaContext(coverages, valueCents);

  return availableSlugs.map(slug => {
    const def = coverageDefBySlug.get(slug);
    const row = existingBySlug.get(slug);
    const mandatory = Boolean(def?.mandatory);
    const limitRule = def?.limitRule ?? null;
    // Automática = derivada pelo motor. O valor exibido é o próprio teto da
    // regra (`<=0.1*basica` → 10% da Básica), a mesma conta que
    // `limit_rule.derive_bound` faz no servidor.
    const derived = Boolean(def?.automatic);
    const maxCents = effectiveMaxCents(limitRule, mandatory, ctx);
    return {
      coverageSlug: slug,
      name: def?.name ?? slug,
      // A Básica é sempre selecionada: o motor recusa o item sem ela
      // (`Proposal._derive_basica` levanta "A cobertura Básica é obrigatória.").
      // A automática também: o motor a força em `_final_rows`.
      selected: mandatory || derived ? true : Boolean(row?.selected),
      lmiDisplay: lmiDisplayFor({
        mandatory,
        derived,
        basicaCents: ctx.basicaCents,
        maxCents,
        saved: row?.lmi,
      }),
      mandatory,
      limitRule,
      maxCents,
      derived,
      ...(mandatory
        ? { basicaHint: describeBasicaRule(limitRule, valueCents) }
        : {}),
      usesIndemnityPeriod: Boolean(def?.usesIndemnityPeriod),
      ...(row?.indemnityMonths != null
        ? { indemnityMonths: row.indemnityMonths }
        : {}),
    };
  });
}

/**
 * Contexto das regras de um item: a Básica derivada do valor do equipamento
 * pela regra da Básica do cadastro — a mesma conta do motor
 * (`limit_rule.derive_basica`). Sem Básica no cadastro, Básica = valor.
 */
function basicaContext(
  coverages: RdCoverageDef[],
  valueCents: number
): { basicaCents: number; equipValueCents: number } {
  const basica = coverages.find(c => c.mandatory);
  return {
    basicaCents: deriveBasicaCents(basica?.limitRule ?? null, valueCents),
    equipValueCents: valueCents,
  };
}

/**
 * Teto efetivo de uma linha: o que a regra impõe, nunca acima da Básica.
 *
 * O limite pela Básica é uma INVARIANTE, não parte do cadastro — o motor a
 * aplica fora do `limit_rule` (`Proposal._validate_limits`) exatamente para
 * uma regra permissiva digitada no admin não conseguir desligá-la. A Básica
 * escapa dela: o teto dela é o próprio `limit_rule` (mais `modality.lmiMax`,
 * checado no valor do equipamento em `step-equipamentos`).
 */
function effectiveMaxCents(
  limitRule: string | null,
  mandatory: boolean,
  ctx: { basicaCents: number; equipValueCents: number }
): number | null {
  const fromRule = resolveLimit(limitRule, ctx);
  if (mandatory) return fromRule;
  return fromRule === null
    ? ctx.basicaCents
    : Math.min(fromRule, ctx.basicaCents);
}

/** LMI a exibir: Básica = derivada da regra, automática = teto da regra, resto = salvo. */
function lmiDisplayFor({
  mandatory,
  derived,
  basicaCents,
  maxCents,
  saved,
}: {
  mandatory: boolean;
  derived: boolean;
  basicaCents: number;
  maxCents: number | null;
  saved?: number;
}): string {
  if (mandatory) return centsToBRL(basicaCents);
  if (derived) return maxCents === null ? '' : centsToBRL(maxCents);
  return saved ? centsToBRL(saved) : '';
}

/**
 * Recalcula tetos e linhas derivadas contra um novo valor de equipamento.
 *
 * O formulário de "Adicionar equipamento" monta a matriz ANTES de o corretor
 * digitar o Valor (e guarda as linhas que ele já editou), então o valor chega
 * depois das linhas. Sem recalcular, os tetos ficavam presos no valor de
 * quando a matriz nasceu — a tela mostrava "máximo R$ 0,00" em toda linha com
 * o campo Valor já exibindo R$ 300.000,00, e a Despesa de Salvamento saía
 * zerada.
 *
 * Só o que é derivado é reescrito: LMI digitado em linha comum é preservado.
 */
export function recomputeLimits(
  rows: CoverageRow[],
  valueCents: number
): CoverageRow[] {
  const basicaRule = rows.find(row => row.mandatory)?.limitRule ?? null;
  const ctx = {
    basicaCents: deriveBasicaCents(basicaRule, valueCents),
    equipValueCents: valueCents,
  };
  return rows.map(row => {
    const maxCents = effectiveMaxCents(row.limitRule, row.mandatory, ctx);
    return {
      ...row,
      maxCents,
      ...(row.mandatory
        ? { basicaHint: describeBasicaRule(row.limitRule, valueCents) }
        : {}),
      lmiDisplay: lmiDisplayFor({
        mandatory: row.mandatory,
        derived: row.derived,
        basicaCents: ctx.basicaCents,
        maxCents,
        saved: brlInputToCents(row.lmiDisplay) || undefined,
      }),
    };
  });
}

/**
 * Estado inicial da matriz de um item JÁ existente — ver
 * `buildRowsForModality`, do qual esta é a especialização que lê modalidade,
 * valor e coberturas salvas do próprio item.
 */
export function buildInitialRows(
  item: RdProposalEquipment,
  rateGrid: RdModalityCoverages,
  coverages: RdCoverageDef[]
): CoverageRow[] {
  return buildRowsForModality(
    item.modalitySlug,
    item.value,
    rateGrid,
    coverages,
    item.coverages
  );
}

export function buildPayload(rows: CoverageRow[]): RdCoverageSelectionPayload {
  const coverages: RdCoverageSelectionItem[] = rows.map(row => ({
    coverageSlug: row.coverageSlug,
    selected: row.selected,
    lmi: brlInputToCents(row.lmiDisplay),
    // O período tem de ir em TODO salvamento: o motor apaga e reinsere as
    // linhas do item a cada PUT, e a matriz é reenviada inteira a cada
    // autosave — omitir aqui faria digitar no LMI de OUTRA linha apagar o
    // prazo recém-escolhido.
    ...(row.indemnityMonths != null
      ? { indemnityMonths: row.indemnityMonths }
      : {}),
  }));
  return { coverages };
}

/**
 * Alterna a seleção de uma linha. Coberturas obrigatórias (Básica) e
 * automáticas (Despesa de Salvamento) são imutáveis: desmarcá-las só produzia
 * um estado que o motor desfaz na gravação — 400 ("A cobertura Básica é
 * obrigatória.") no primeiro caso, re-seleção silenciosa no segundo.
 */
/** A linha sem o período — usada nos dois caminhos que a desmarcam. */
function semPeriodo(row: CoverageRow): CoverageRow {
  const copia = { ...row };
  delete copia.indemnityMonths;
  return copia;
}

export function applyToggle(rows: CoverageRow[], slug: string): CoverageRow[] {
  const target = rows.find(row => row.coverageSlug === slug);
  if (target?.mandatory || target?.derived) return rows;
  return rows.map(row => {
    if (row.coverageSlug !== slug) return row;
    const selected = !row.selected;
    // Desmarcar LIMPA o período: remarcar não deve herdar em silêncio o
    // prazo de uma escolha anterior — é termo contratual, e o motor também
    // o limpa na gravação.
    return selected ? { ...row, selected } : { ...semPeriodo(row), selected };
  });
}

/**
 * Registra o período indenitário escolhido numa linha.
 *
 * As três opções ficam à vista (decisão do PO, 2026-09-04): quem oferece a
 * lista é o cadastro do motor, e esta função só guarda a escolha. O motor
 * revalida contra o cadastro e barra a COTAÇÃO quando falta — a gravação
 * aceita vazio, senão uma proposta antiga travaria a edição de outra linha.
 */
export function applyIndemnityChange(
  rows: CoverageRow[],
  slug: string,
  months: number
): CoverageRow[] {
  return rows.map(row =>
    row.coverageSlug === slug && row.usesIndemnityPeriod
      ? { ...row, indemnityMonths: months }
      : row
  );
}

/** Linhas selecionadas que pedem período e ainda não têm um. */
export function missingIndemnitySlugs(rows: CoverageRow[]): string[] {
  return rows
    .filter(
      row => row.selected && row.usesIndemnityPeriod && !row.indemnityMonths
    )
    .map(row => row.coverageSlug);
}

/**
 * Aplica o valor digitado numa linha, já mascarado como moeda.
 *
 * A seleção acompanha o valor digitado, nos dois sentidos:
 *
 * - valor > 0 numa cobertura desmarcada MARCA a cobertura. Digitar uma I.S.
 *   é dizer que se quer a cobertura; antes o valor era persistido com
 *   `selected: false` e ignorado no prêmio, no LMG e na apólice, sem aviso.
 * - valor zerado numa cobertura marcada DESMARCA. Sem isso o espelho do
 *   mesmo problema aparecia: cobertura selecionada com LMI 0, aceita em
 *   silêncio pelo autosave.
 *
 * Coberturas obrigatórias (Básica) ficam de fora do desmarque: elas têm
 * regra própria — LMI > 0 é exigido e bloqueia o "Próximo" (fix C4) —, e
 * desmarcá-las esconderia esse erro.
 *
 * Linha DERIVADA (automática) ignora a digitação por inteiro: o valor dela é
 * calculado pelo motor e qualquer coisa digitada seria descartada na
 * gravação. O input é readonly, então isto é só a defesa do estado.
 */
export function applyLmiChange(
  rows: CoverageRow[],
  slug: string,
  raw: string
): CoverageRow[] {
  const formatted = formatCurrencyInputValue(raw);
  return rows.map(row => {
    if (row.coverageSlug !== slug || row.derived) return row;
    const cents = brlInputToCents(formatted);
    const selected = row.mandatory ? row.selected : cents > 0;
    // Zerar o LMI desmarca, e desmarcar limpa o período — mesma regra do
    // `applyToggle`, pelo mesmo motivo.
    if (!selected) {
      return { ...semPeriodo(row), lmiDisplay: formatted, selected };
    }
    return { ...row, lmiDisplay: formatted, selected };
  });
}

/**
 * Linhas selecionadas cujo LMI passa do teto da cobertura.
 *
 * Antecipa a recusa do motor ("I.S. acima do limite para esta cobertura.")
 * para o "Próximo" barrar antes do PUT. A autoridade continua sendo o motor —
 * ele revalida e o erro dele segue mapeado por linha em `coverage-matrix`.
 */
export function overLimitSlugs(rows: CoverageRow[]): string[] {
  return rows
    .filter(
      row =>
        row.selected &&
        row.maxCents !== null &&
        brlInputToCents(row.lmiDisplay) > row.maxCents
    )
    .map(row => row.coverageSlug);
}

/** Alguma linha selecionada passou do seu teto — ver `overLimitSlugs`. */
export function isOverLimit(rows: CoverageRow[]): boolean {
  return overLimitSlugs(rows).length > 0;
}

/** Linha `mandatory` (Básica) presente e com LMI 0/vazio — ver `BASICA_REQUIRED_MESSAGE`. */
export function isBasicaInvalid(rows: CoverageRow[]): boolean {
  const basicaRow = rows.find(row => row.mandatory);
  return basicaRow !== undefined && brlInputToCents(basicaRow.lmiDisplay) <= 0;
}

/**
 * Mapeia uma chave de erro de campo do backend (`ValidationError.fieldErrors`)
 * para o índice (0-based) da linha na matriz, quando possível. Três formatos:
 * - o SLUG da cobertura — formato atual (o motor passou a identificá-la
 *   assim; antes usava o id do catálogo global, que o consumidor não tem como
 *   mapear). O BFF converte para camelCase no caminho, então comparamos de
 *   forma normalizada.
 * - `coverageN` (1-based, ex.: `coverage1`) — posição do item no array
 *   `coverages` submetido (mesma ordem de `rows`/`buildPayload`).
 * - `coverages.<index>.lmi` (0-based) — formato defensivo, mantido caso uma
 *   validação estrutural genérica algum dia use esse shape.
 * Retorna `null` quando a chave não bate com nenhum dos três, para o chamador
 * tratar a mensagem como não mapeável (banner, em vez de descartada).
 */
export function resolveCoverageFieldRowIndex(
  field: string,
  rows: { coverageSlug: string }[]
): number | null {
  const normalize = (value: string) => value.replace(/_/g, '').toLowerCase();
  const bySlug = rows.findIndex(
    row => normalize(row.coverageSlug) === normalize(field)
  );
  if (bySlug !== -1) return bySlug;

  const positional = /^coverage(\d+)$/.exec(field);
  if (positional) return Number(positional[1]) - 1;

  const dotPath = /^coverages\.(\d+)\.lmi$/.exec(field);
  if (dotPath) return Number(dotPath[1]);

  return null;
}
